import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';

const getWeekNumber = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
};

/**
 * Auto-reconcile the streak on app open.
 *
 * Rules:
 * - Opening the app NEVER increments the streak.
 * - If `last_active_date` is missing or already today/future → no-op.
 * - daysMissed = differenceInCalendarDays(today, lastActive) - 1.
 *   (lastActive=yesterday → 0 missed; lastActive=2 days ago → 1 missed.)
 * - If freezes >= daysMissed: consume that many freezes, preserve
 *   `current_streak`, and close the gap by moving `last_active_date` to
 *   YESTERDAY. The displayed number stays correct.
 * - If freezes < daysMissed: the streak is broken. Reset `current_streak`
 *   to 0 and move `last_active_date` to YESTERDAY. Freezes are PRESERVED
 *   (we never spend freezes that can't fully save the streak).
 * - IMPORTANT: the gap is closed to YESTERDAY, never today. Writing today
 *   would make `addXP` treat the day as already counted, so a real workout
 *   or food log later that same day would not increment the streak (the
 *   "streak stuck" bug). Yesterday keeps it idempotent (daysMissed = 0 on
 *   the next run) while leaving today claimable by real activity.

 * - Also refills the weekly freeze allotment (1/week) if a new week has
 *   started and the user currently has none.
 */
export function useStreakReconcile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const ranForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    if (ranForUserRef.current === user.id) return;
    ranForUserRef.current = user.id;

    let cancelled = false;

    (async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('current_streak, longest_streak, streak_freezes, last_freeze_week, last_active_date')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled || !profile) return;

        const p = profile as any;
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        const today = parseISO(todayStr);
        const lastActiveStr: string | null = p.last_active_date ?? null;
        const week = getWeekNumber();

        let nextFreezes: number = Math.max(0, Number(p.streak_freezes ?? 0));
        let nextLastFreezeWeek: number | null = p.last_freeze_week ?? null;
        const updates: Record<string, unknown> = {};

        // Weekly freeze refill: if a new week and user has 0 freezes, top up to 1.
        if (nextLastFreezeWeek !== week && nextFreezes < 1) {
          nextFreezes = 1;
          updates.streak_freezes = nextFreezes;
        }

        // Reconcile missed days.
        if (lastActiveStr) {
          let daysMissed = 0;
          try {
            const lastActive = parseISO(lastActiveStr);
            daysMissed = differenceInCalendarDays(today, lastActive) - 1;
          } catch {
            daysMissed = 0;
          }

          if (daysMissed > 0) {
            const currentStreak = Number(p.current_streak ?? 0);
            // Close the gap to YESTERDAY, never today — today must stay
            // claimable by real activity (see header comment).
            const yesterdayStr = format(subDays(today, 1), 'yyyy-MM-dd');
            if (nextFreezes >= daysMissed && currentStreak > 0) {
              // Consume freezes, preserve streak, close the gap.
              nextFreezes = Math.max(0, nextFreezes - daysMissed);
              updates.streak_freezes = nextFreezes;
              updates.last_freeze_week = week;
              updates.streak_freeze_used_at = new Date().toISOString();
              updates.last_active_date = yesterdayStr;
              // current_streak preserved (no write needed, but write to be explicit)
              updates.current_streak = currentStreak;
            } else {
              // Can't fully cover — break the streak, preserve freezes.
              updates.current_streak = 0;
              updates.last_active_date = yesterdayStr;
            }
          }
        }

        if (Object.keys(updates).length === 0) return;

        await supabase.from('profiles').update(updates as any).eq('id', user.id);
        if (cancelled) return;
        queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      } catch {
        // Non-fatal — leave the displayed value as-is; next open retries.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, queryClient]);
}
